import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOHeadProps {
  title?: string;
  description?: string;
  /** @deprecated Google ignores meta keywords since 2009. Kept for backward compatibility. */
  keywords?: string;
  image?: string;
  url?: string;
  type?: string;
  noIndex?: boolean;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogType?: string;
  /** Additional JSON-LD schema object(s) to inject */
  schema?: object | object[];
  softwareOfferDescription?: string;
  article?: {
    publishedTime?: string;
    modifiedTime?: string;
    author?: string;
    section?: string;
    tags?: string[];
  };
}

const SEOHead: React.FC<SEOHeadProps> = ({
  title = 'Gestión de Proyectos con IA Gratis | ProyectMind',
  description = 'Software gestión proyectos con IA 100% gratis. Gantt, Kanban, automatización y análisis predictivo para equipos en México y LATAM.',
  image = '/og-image.png',
  url = 'https://proyectmind.online',
  type = 'website',
  noIndex = false,
  ogTitle,
  ogDescription,
  ogImage,
  ogType,
  schema,
  softwareOfferDescription = 'Plan 100% GRATIS - Sin límites de usuarios ni proyectos',
  article
}) => {
  const fullTitle = title.includes('ProyectMind') ? title : `${title} | ProyectMind`;
  // Ensure trailing slash on homepage canonical
  const normalizedUrl = url === '/' ? '/' : url;
  const fullUrl = normalizedUrl.startsWith('http') ? normalizedUrl : `https://proyectmind.online${normalizedUrl}`;
  const resolvedOgImage = ogImage || image;
  const fullImage = resolvedOgImage.startsWith('http') ? resolvedOgImage : `https://proyectmind.online${resolvedOgImage}`;
  const resolvedOgTitle = ogTitle || fullTitle;
  const resolvedOgDescription = ogDescription || description;
  const resolvedOgType = ogType || type;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={fullUrl} />
      
      {/* Hreflang tags */}
      <link rel="alternate" hrefLang="es-MX" href={fullUrl} />
      <link rel="alternate" hrefLang="es-CO" href={fullUrl} />
      <link rel="alternate" hrefLang="es-AR" href={fullUrl} />
      <link rel="alternate" hrefLang="es-ES" href={fullUrl} />
      <link rel="alternate" hrefLang="es" href={fullUrl} />
      <link rel="alternate" hrefLang="x-default" href={fullUrl} />
      
      {/* Robots */}
      {noIndex && <meta name="robots" content="noindex,nofollow" />}
      {!noIndex && <meta name="robots" content="index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1" />}
      
      {/* Geo Targeting for LATAM */}
      <meta name="geo.region" content="MX" />
      <meta name="geo.placename" content="México" />
      <meta httpEquiv="content-language" content="es-MX" />
      
      {/* Open Graph */}
      <meta property="og:title" content={resolvedOgTitle} />
      <meta property="og:description" content={resolvedOgDescription} />
      <meta property="og:image" content={fullImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={resolvedOgTitle} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:type" content={resolvedOgType} />
      <meta property="og:site_name" content="ProyectMind" />
      <meta property="og:locale" content="es_MX" />
      <meta property="og:locale:alternate" content="es_ES" />
      <meta property="og:locale:alternate" content="es_CO" />
      <meta property="og:locale:alternate" content="es_AR" />
      
      {/* Article specific Open Graph */}
      {article && (
        <>
          <meta property="article:published_time" content={article.publishedTime} />
          <meta property="article:modified_time" content={article.modifiedTime} />
          {article.author && <meta property="article:author" content={article.author} />}
          {article.section && <meta property="article:section" content={article.section} />}
          {article.tags?.map((tag, index) => (
            <meta key={index} property="article:tag" content={tag} />
          ))}
        </>
      )}
      
      {/* Twitter Cards */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImage} />
      <meta name="twitter:site" content="@proyectmind" />
      <meta name="twitter:creator" content="@proyectmind" />
      
      {/* Additional SEO */}
      <meta name="author" content="ProyectMind" />
      <meta name="theme-color" content="#C07B20" />
      
      {/* Structured Data - SoftwareApplication */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "ProyectMind",
          "description": description,
          "url": fullUrl,
          "image": fullImage,
          "applicationCategory": "BusinessApplication",
          "applicationSubCategory": "Project Management Software",
          "operatingSystem": "Web Browser",
          "softwareVersion": "2.0",
          "datePublished": "2024-01-01",
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "MXN",
            "description": softwareOfferDescription,
            "availability": "https://schema.org/InStock"
          },
          "provider": {
            "@type": "Organization",
            "name": "ProyectMind",
            "url": "https://proyectmind.online"
          },
          "featureList": [
            "Diagramas Gantt Interactivos",
            "IA Predictiva Avanzada",
            "Análisis de Riesgos Automático",
            "Automatización de Tareas",
            "Metodologías Híbridas (Scrum, Kanban, Waterfall)",
            "Dashboard en Tiempo Real",
            "Gestión de Recursos y Equipos",
            "Reportes Automáticos PDF",
            "Colaboración en Tiempo Real"
          ]
        })}
      </script>
      
      {/* Structured Data - WebSite with SearchAction */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": "ProyectMind",
          "url": "https://proyectmind.online",
          "description": "Software de gestión de proyectos con IA 100% gratis para México y Latinoamérica",
          "inLanguage": "es-MX",
          "potentialAction": {
            "@type": "SearchAction",
            "target": {
              "@type": "EntryPoint",
              "urlTemplate": "https://proyectmind.online/blog?q={search_term_string}"
            },
            "query-input": "required name=search_term_string"
          }
        })}
      </script>
      
      {/* Structured Data - BreadcrumbList (auto 3 levels for nested routes) */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": (() => {
            const items: object[] = [{
              "@type": "ListItem",
              "position": 1,
              "name": "Inicio",
              "item": "https://proyectmind.online"
            }];
            if (url !== '/' && url !== 'https://proyectmind.online') {
              const path = url.startsWith('http') ? new URL(url).pathname : url;
              const segments = path.split('/').filter(Boolean);
              if (segments.length >= 2) {
                const parentMap: Record<string, string> = {
                  'comparativas': 'Comparativas',
                  'blog': 'Blog',
                };
                const parentSlug = segments[0];
                const parentName = parentMap[parentSlug] || parentSlug.charAt(0).toUpperCase() + parentSlug.slice(1);
                items.push({
                  "@type": "ListItem",
                  "position": 2,
                  "name": parentName,
                  "item": `https://proyectmind.online/${parentSlug}`
                });
                items.push({
                  "@type": "ListItem",
                  "position": 3,
                  "name": title.replace(' | ProyectMind', ''),
                  "item": fullUrl
                });
              } else {
                items.push({
                  "@type": "ListItem",
                  "position": 2,
                  "name": title.replace(' | ProyectMind', ''),
                  "item": fullUrl
                });
              }
            }
            return items;
          })()
        })}
      </script>
      
      {/* Additional schema(s) passed via prop */}
      {schema && (Array.isArray(schema) ? schema : [schema]).map((s, i) => (
        <script key={`schema-${i}`} type="application/ld+json">
          {JSON.stringify(s)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEOHead;
